import React, { useEffect } from 'react';
import { Link, Outlet } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo.jsx';
import LanguageToggle from '../components/LanguageToggle.jsx';
import { useBrand } from '../context/TenantContext.jsx';
import { useTranslation } from '../i18n/LanguageContext.jsx';

const HERO_POINTS = [
  { icon: 'fa-solid fa-user-plus', title: 'Refer in minutes', text: 'Submit and track client referrals from any device.' },
  { icon: 'fa-solid fa-peso-sign', title: 'Transparent commissions', text: 'Watch your earnings move from pending to paid.' },
  { icon: 'fa-solid fa-shield-halved', title: 'Bank-grade security', text: 'Protected sign-in with verification when it matters.' },
];

/** Privacy / Terms links under the sign-in and registration forms. */
function LegalLinks() {
  const { t } = useTranslation();
  return (
    <span className="auth-split__legal">
      <Link to="/privacy">{t('public.nav.privacy')}</Link>
      <Link to="/terms">{t('public.nav.terms')}</Link>
    </span>
  );
}

export default function PublicLayout() {
  const brand = useBrand();

  useEffect(() => {
    document.body.classList.add('public-page');
    return () => document.body.classList.remove('public-page');
  }, []);

  return (
    <div className="auth-split">
      {/* Brand / hero panel — hidden on small screens, shown from lg up */}
      <aside className="auth-split__hero">
        <div className="auth-split__hero-top">
          <Link to="/" className="auth-split__brand" aria-label={`${brand.name} — home`}>
            <BrandLogo size="lg" variant="light" />
          </Link>
        </div>

        <div className="auth-split__hero-body">
          <h2 className="auth-split__hero-title">{brand.tagline}</h2>
          <p className="auth-split__hero-lead">
            {brand.name} gives connectors and staff a single, secure workspace to move business
            forward.
          </p>

          <ul className="auth-split__points">
            {HERO_POINTS.map((p) => (
              <li className="auth-split__point" key={p.title}>
                <span className="auth-split__point-icon">
                  <i className={p.icon} aria-hidden="true" />
                </span>
                <span>
                  <span className="auth-split__point-title">{p.title}</span>
                  <span className="auth-split__point-text">{p.text}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="auth-split__hero-foot">
          © {new Date().getFullYear()} {brand.name}. All rights reserved.
          <LegalLinks />
        </div>
      </aside>

      {/* Form panel */}
      <div className="auth-split__main">
        <header className="auth-split__topbar">
          {/* Compact brand shown only when the hero panel is hidden (mobile) */}
          <Link
            to="/"
            className="auth-split__brand-compact"
            aria-label={`${brand.name} — home`}
          >
            <BrandLogo size="sm" variant="dark" />
          </Link>
          <div className="ms-auto">
            <LanguageToggle variant="dark" />
          </div>
        </header>

        <main className="auth-split__form">
          <Outlet />
        </main>

        <footer className="auth-split__foot">
          © {new Date().getFullYear()} {brand.name}. All rights reserved.
          <LegalLinks />
        </footer>
      </div>
    </div>
  );
}
